import { Divider, Dropdown, IconButton } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { AwayStatus, presenceApi } from 'apis/presence';
import { getChannelName } from 'utils/channels';
import { mediaAttributes, MetaWorkingMedia } from 'utils/channels/media';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { listToAPStyleComma } from 'utils/formatting/string';
import {
  requestMicrophoneAccess,
  RequestMicrophoneAccessError,
} from 'utils/telephony/microphone';

import {
  getSimulaneousRingHelpText,
  getSimulaneousRingHelpTitle,
} from '../utils/simultaneousRingHelpText';

import { PresenceDropdownItem, PresenceToggle } from './PresenceDropdownItem';
import { PresenceStatusItem } from './PresenceStatusItem';
import styles from './presenceDropdown.module.scss';

const channelLabelMap = {
  voicemail: 'Voicemail',
  email: 'Email',
  contactform: 'Contact form',
  callback: 'Callback',
  pstnphone: 'Phone',
  interactivechat: 'Chat',
  speak: 'Voice',
  messaging: 'Messaging',
};

type Props = {
  metaWorkingMedia: MetaWorkingMedia[];
  hasAccess: boolean;
  hasPhoneChannel: boolean;
  errorMicrophoneErrorShowModal: (payload?: any) => void;
  showLogoutModal: () => void;
  presence: any;
  oppositePresenceStatus: string;
  telephony: any;
  presenceSetMedia: any;
  presenceSetMediaChannel: any;
  setCustomAwayStatus: (
    awayStatus: Pick<AwayStatus, 'id' | 'label' | 'emoji'>,
  ) => void;
  isPresenceStatusLoading: boolean;
  personalToggleRememberMicrophoneAccess: any;
  validPhoneNumber: any;
  isInTrial: boolean;
  profile: any;
  toggleSimultaneousRing: (location: string) => void;
  changeYourStatus: (location: 'topbar' | 'topbarDropdown') => void;
};

/** Prefix for generating DOM element IDs for navigable items. */
const DROPDOWN_ITEM_ID_PREFIX = 'presence-dropdown-item-';

export const PresenceDropdown = ({
  metaWorkingMedia,
  errorMicrophoneErrorShowModal,
  presence,
  hasAccess,
  hasPhoneChannel,
  oppositePresenceStatus,
  telephony,
  presenceSetMedia,
  presenceSetMediaChannel,
  setCustomAwayStatus,
  personalToggleRememberMicrophoneAccess,
  validPhoneNumber,
  isInTrial,
  isPresenceStatusLoading,
  profile,
  toggleSimultaneousRing,
  changeYourStatus,
  showLogoutModal,
}: Props): JSX.Element => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [moreAwayStatusesOpen, setMoreAwayStatusesOpen] = useState(false);
  const { custom_away_status_id } = presence?.presence_status;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch away statuses for keyboard navigation
  const flags = featureFlags.get(['custom-away-status']);
  const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
    { isDeactivated: false },
    {
      skip: !flags.customAwayStatus,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    },
  );

  // Split away statuses same as PresenceStatusItem does
  const maxNumberOfVisibleItems = 6;
  const [firstNStatuses, additionalStatuses] =
    awayStatuses.length > maxNumberOfVisibleItems
      ? [
          awayStatuses.slice(0, maxNumberOfVisibleItems - 1),
          awayStatuses.slice(maxNumberOfVisibleItems - 1),
        ]
      : [awayStatuses, []];

  useClickAway(dropdownRef, () => {
    setShowDropdown(false);
    setMoreAwayStatusesOpen(false);
  });

  const handleMediaClick = useCallback(
    (metaMedia: MetaWorkingMedia): void => {
      if (metaMedia.name === 'speak' && telephony.isMicrophoneBlocked) {
        requestMicrophoneAccess()
          .then(errorMicrophoneErrorShowModal)
          .catch(errorMicrophoneErrorShowModal)
          .finally(() => {
            // Remove the remember my microphone access.
            personalToggleRememberMicrophoneAccess({
              value: false,
            });
          });
      } else {
        const working = !metaMedia.working;
        presenceSetMedia({ media: metaMedia.name, working }).then(() => {
          ampli.presenceMediaUpdated({
            media: metaMedia.name,
            enabled: working,
            location: 'topbarDropdown',
          });
        });
      }
    },
    [
      errorMicrophoneErrorShowModal,
      personalToggleRememberMicrophoneAccess,
      presenceSetMedia,
      telephony.isMicrophoneBlocked,
    ],
  );

  // Build list of navigable item IDs and their actions
  const getNavigableItems = useCallback(() => {
    const items: Array<{ id: string; onActivate: () => void }> = [];

    // Status item
    items.push({
      id: `${DROPDOWN_ITEM_ID_PREFIX}status`,
      onActivate: () => changeYourStatus('topbarDropdown'),
    });

    // Away status items (only when status is 'away')
    if (presence.status === 'away') {
      // First N away statuses
      firstNStatuses.forEach(({ id, label, emoji }) => {
        items.push({
          id: `${DROPDOWN_ITEM_ID_PREFIX}away-status-${id}`,
          onActivate: () => setCustomAwayStatus({ id, label, emoji }),
        });
      });

      // "More away statuses" item (if there are additional statuses)
      if (additionalStatuses.length > 0) {
        items.push({
          id: `${DROPDOWN_ITEM_ID_PREFIX}more-away-statuses`,
          onActivate: () => {
            setMoreAwayStatusesOpen((prev) => !prev);
          },
        });

        // Additional away statuses (when "More away statuses" is open)
        if (moreAwayStatusesOpen) {
          additionalStatuses.forEach(({ id, label, emoji }) => {
            items.push({
              id: `${DROPDOWN_ITEM_ID_PREFIX}additional-away-status-${id}`,
              onActivate: () => setCustomAwayStatus({ id, label, emoji }),
            });
          });
        }
      }
    }

    // Media items
    metaWorkingMedia.forEach((metaMedia) => {
      items.push({
        id: `${DROPDOWN_ITEM_ID_PREFIX}media-${metaMedia.name}`,
        onActivate: () => handleMediaClick(metaMedia),
      });
    });

    // Simultaneous ring (if phone channel exists)
    if (hasPhoneChannel) {
      items.push({
        id: `${DROPDOWN_ITEM_ID_PREFIX}simultaneous-ring`,
        onActivate: () => toggleSimultaneousRing('topbarDropdown'),
      });
    }

    // Sign out
    items.push({
      id: `${DROPDOWN_ITEM_ID_PREFIX}sign-out`,
      onActivate: showLogoutModal,
    });

    return items;
  }, [
    additionalStatuses,
    changeYourStatus,
    firstNStatuses,
    handleMediaClick,
    hasPhoneChannel,
    metaWorkingMedia,
    moreAwayStatusesOpen,
    presence.status,
    setCustomAwayStatus,
    showLogoutModal,
    toggleSimultaneousRing,
  ]);

  // Reset focus when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      setFocusedIndex(0);
      buttonRef.current?.focus();
    }
  }, [showDropdown]);

  // Keyboard navigation
  useEffect(() => {
    if (!showDropdown) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const items = getNavigableItems();
      if (items.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          event.stopPropagation();
          setFocusedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
          break;

        case 'ArrowUp':
          event.preventDefault();
          event.stopPropagation();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
          break;

        case 'Enter':
          event.preventDefault();
          event.stopPropagation();
          items[focusedIndex]?.onActivate();
          break;

        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          setShowDropdown(false);
          break;

        case 'Tab':
          setShowDropdown(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [showDropdown, focusedIndex, getNavigableItems]);

  // Get the currently focused item ID for aria-activedescendant
  const navigableItems = getNavigableItems();
  const activeDescendantId =
    focusedIndex >= 0 && focusedIndex < navigableItems.length
      ? navigableItems[focusedIndex].id
      : undefined;

  const handleChannelClick = (
    conversation_channel: ConversationsChannels,
  ): void => {
    const workingMedia = presence.working_channels.find(
      ({ conversation_channel: workingChannel }) =>
        workingChannel === conversation_channel,
    );
    if (
      conversation_channel === 'pstnphone' ||
      conversation_channel === 'callback'
    ) {
      if (telephony.isMicrophoneBlocked === true) {
        requestMicrophoneAccess()
          .then(() => {
            errorMicrophoneErrorShowModal();
          })
          .catch((microphoneResponse: RequestMicrophoneAccessError) => {
            errorMicrophoneErrorShowModal(microphoneResponse);
          });
      } else {
        presenceSetMediaChannel({
          working: !workingMedia.working,
          channel: conversation_channel,
        });
      }
    } else {
      presenceSetMediaChannel({
        working: !workingMedia.working,
        channel: conversation_channel,
      });
    }
  };

  const canToggleSimultaneousRing =
    hasAccess && isInTrial === false && validPhoneNumber === true;
  const simultaneousRingTitle = getSimulaneousRingHelpTitle(
    validPhoneNumber,
    isInTrial,
  );

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <IconButton
        ref={buttonRef}
        className={classNames(styles.dropdownButton, {
          [styles.dropdownButtonFocused]: showDropdown,
        })}
        icon="angle-down"
        variant="secondary"
        onClick={() => {
          setShowDropdown(!showDropdown);
        }}
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        data-testid="presence-dropdown-button"
      ></IconButton>
      <Dropdown isOpen={showDropdown} data-testid="presence-dropdown-menu">
        <ul
          role="menu"
          aria-activedescendant={activeDescendantId}
          className={styles.dropdown}
        >
          <PresenceStatusItem
            awayStatusId={custom_away_status_id}
            isStatusLoading={isPresenceStatusLoading}
            status={presence.status}
            oppositeStatus={oppositePresenceStatus}
            changeOwnStatus={() => changeYourStatus('topbarDropdown')}
            setCustomAwayStatus={setCustomAwayStatus}
            showPresenceDropdown={() => setShowDropdown(true)}
            itemId={`${DROPDOWN_ITEM_ID_PREFIX}status`}
            focusedItemId={activeDescendantId}
            moreAwayStatusesOpen={moreAwayStatusesOpen}
            onMoreAwayStatusesOpenChange={setMoreAwayStatusesOpen}
          />

          <Divider />

          {metaWorkingMedia.map((metaMedia) => {
            const tooltip = listToAPStyleComma(
              metaMedia.channels.map(getChannelName),
            );
            const mediaName = mediaAttributes[metaMedia.name].name;
            const tooltipButton = hasAccess
              ? `Turn ${mediaName} ${metaMedia.working ? 'off' : 'on'}`
              : 'Restricted by organization';
            return (
              <PresenceDropdownItem
                key={metaMedia.name}
                itemId={`${DROPDOWN_ITEM_ID_PREFIX}media-${metaMedia.name}`}
                focusedItemId={activeDescendantId}
                icon={mediaAttributes[metaMedia.name].icon}
                text={channelLabelMap[metaMedia.name]}
                tooltipText={tooltip}
                subitems={
                  metaMedia.name === 'speak'
                    ? [
                        <PresenceDropdownItem
                          key={'speak'}
                          icon="voicemail"
                          text="Voicemail"
                          isSubitem
                        >
                          <PresenceToggle
                            id="voicemail"
                            active={
                              presence.working_channels.find(
                                ({ conversation_channel }) =>
                                  conversation_channel === 'voicemail',
                              )?.working
                            }
                            disabled={!hasAccess}
                            isAway={presence.status !== 'working'}
                            onToggle={() => {
                              handleChannelClick('voicemail');
                            }}
                          />
                        </PresenceDropdownItem>,
                      ]
                    : undefined
                }
              >
                <PresenceToggle
                  id={metaMedia.name}
                  active={metaMedia.working}
                  isAway={presence.status !== 'working'}
                  disabled={!hasAccess}
                  onToggle={() => {
                    handleMediaClick(metaMedia);
                  }}
                  tooltipText={tooltipButton}
                />
              </PresenceDropdownItem>
            );
          })}

          <Divider />

          {hasPhoneChannel && (
            <>
              <PresenceDropdownItem
                itemId={`${DROPDOWN_ITEM_ID_PREFIX}simultaneous-ring`}
                focusedItemId={activeDescendantId}
                icon="simultaneous"
                text="Simultaneous Ring"
                helpText={getSimulaneousRingHelpText(
                  validPhoneNumber,
                  isInTrial,
                  profile.phone_number,
                )}
                helpTitle={simultaneousRingTitle}
                disabled={!canToggleSimultaneousRing}
                tooltipText={
                  !hasAccess
                    ? 'Restricted by organization'
                    : !canToggleSimultaneousRing
                      ? simultaneousRingTitle
                      : ''
                }
              >
                <PresenceToggle
                  id="simultaneous-ring"
                  active={
                    presence.presence_status.simultaneous_ringing === true
                  }
                  disabled={!canToggleSimultaneousRing}
                  onToggle={() => toggleSimultaneousRing('topbarDropdown')}
                  isAway={presence.status !== 'working'}
                  tooltipText={!hasAccess ? 'Restricted by organization' : ''}
                />
              </PresenceDropdownItem>

              <Divider />
            </>
          )}

          <PresenceDropdownItem
            itemId={`${DROPDOWN_ITEM_ID_PREFIX}sign-out`}
            focusedItemId={activeDescendantId}
            icon="sign-out"
            text="Sign out"
            onClick={showLogoutModal}
          />
        </ul>
      </Dropdown>
    </div>
  );
};
